import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { AlertTriangle, FileWarning, ExternalLink, RefreshCw, ShieldCheck,
         Search, X } from "lucide-react";

// Cosa mostrare: tutto, solo i mancanti o solo gli scaduti. Con quaranta
// fascicoli l'elenco completo e' illeggibile, e chi carica i documenti di
// solito sta lavorando su una cosa sola per volta.
const VISTE = [
  { id: "tutto", label: "Tutto" },
  { id: "mancanti", label: "Solo mancanti" },
  { id: "scaduti", label: "Solo scaduti" },
];

export default function Discrepanze() {
  const [rows, setRows] = useState([]);
  const [caricando, setCaricando] = useState(true);

  // Si ricarica anche al rientro sulla pagina: caricato un documento in un
  // altro punto dell'app, qui il conteggio dev'essere gia' aggiornato.
  const carica = useCallback(async () => {
    setCaricando(true);
    try {
      const { data } = await api.get("/dashboard/discrepancies");
      setRows(data);
    } finally { setCaricando(false); }
  }, []);

  const [categorie, setCategorie] = useState(null);
  const [mostraCategorie, setMostraCategorie] = useState(false);
  const [cerca, setCerca] = useState("");
  const [tipo, setTipo] = useState(null);      // documento su cui si sta lavorando
  const [vista, setVista] = useState("tutto");

  useEffect(() => { carica(); }, [carica]);
  useEffect(() => {
    api.get("/employees/documents/categories-in-use")
      .then(({ data }) => setCategorie(data.categorie)).catch(() => {});
  }, [rows]);
  useEffect(() => {
    const suFuoco = () => { if (document.visibilityState === "visible") carica(); };
    window.addEventListener("focus", suFuoco);
    document.addEventListener("visibilitychange", suFuoco);
    return () => {
      window.removeEventListener("focus", suFuoco);
      document.removeEventListener("visibilitychange", suFuoco);
    };
  }, [carica]);

  // Quante persone aspettano ciascun documento: e' il numero che dice da dove
  // conviene cominciare a caricare.
  const conteggi = useMemo(() => {
    const mappa = new Map();
    for (const r of rows) {
      for (const m of r.missing || []) {
        const v = mappa.get(m) || { tipo: m, mancanti: 0, scaduti: 0 };
        v.mancanti += 1;
        mappa.set(m, v);
      }
      for (const e of r.expired || []) {
        const v = mappa.get(e) || { tipo: e, mancanti: 0, scaduti: 0 };
        v.scaduti += 1;
        mappa.set(e, v);
      }
    }
    return [...mappa.values()].sort(
      (a, b) => (b.mancanti + b.scaduti) - (a.mancanti + a.scaduti) || a.tipo.localeCompare(b.tipo));
  }, [rows]);

  const totali = useMemo(() => ({
    mancanti: rows.reduce((n, r) => n + (r.missing || []).length, 0),
    scaduti: rows.reduce((n, r) => n + (r.expired || []).length, 0),
  }), [rows]);

  // Il filtro non nasconde solo le persone: nasconde anche le righe che non
  // riguardano il documento scelto, altrimenti la scheda resterebbe lunga
  // uguale e non si capirebbe cosa manca davvero.
  const visibili = useMemo(() => {
    const testo = cerca.trim().toLowerCase();
    return rows.map(r => {
      const missing = (r.missing || []).filter(
        m => (vista !== "scaduti") && (!tipo || m === tipo));
      const expired = (r.expired || []).filter(
        e => (vista !== "mancanti") && (!tipo || e === tipo));
      return { ...r, missing, expired };
    }).filter(r => {
      if (r.missing.length === 0 && r.expired.length === 0) return false;
      if (!testo) return true;
      return (r.full_name || "").toLowerCase().includes(testo)
          || (r.badge_number || "").toLowerCase().includes(testo)
          || (r.role || "").toLowerCase().includes(testo);
    });
  }, [rows, cerca, tipo, vista]);

  const filtriAttivi = cerca.trim() || tipo || vista !== "tutto";
  const azzera = () => { setCerca(""); setTipo(null); setVista("tutto"); };

  return (
    <div className="space-y-6" data-testid="discrepanze-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-red-600 mb-2">Dipendenti</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Discrepanze documenti</h1>
          <p className="text-sm text-slate-500 mt-1">
            {caricando
              ? "Controllo in corso..."
              : `${rows.length} dipendenti · ${totali.mancanti} document${totali.mancanti === 1 ? "o" : "i"} mancant${totali.mancanti === 1 ? "e" : "i"} · ${totali.scaduti} scadut${totali.scaduti === 1 ? "o" : "i"}`}
          </p>
        </div>
        <Button variant="outline" className="h-11" onClick={carica} disabled={caricando}
                data-testid="discrepanze-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${caricando ? "animate-spin" : ""}`} /> Aggiorna
        </Button>
      </div>

      {conteggi.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs uppercase tracking-widest text-slate-400">
                Cosa manca, e a quante persone
              </div>
              <div className="flex items-center gap-1">
                {VISTE.map(v => (
                  <button key={v.id} type="button" onClick={() => setVista(v.id)}
                          data-testid={`vista-${v.id}`}
                          className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                            vista === v.id
                              ? "bg-slate-900 text-white border-slate-900"
                              : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {conteggi.map(c => {
                const attivo = tipo === c.tipo;
                const mostraMancanti = vista !== "scaduti" && c.mancanti > 0;
                const mostraScaduti = vista !== "mancanti" && c.scaduti > 0;
                if (!mostraMancanti && !mostraScaduti) return null;
                return (
                  <button key={c.tipo} type="button"
                          onClick={() => setTipo(attivo ? null : c.tipo)}
                          data-testid={`conteggio-${c.tipo}`}
                          className={`text-left rounded border px-3 py-2 transition-colors ${
                            attivo
                              ? "border-orange-500 bg-orange-50"
                              : "border-slate-200 hover:bg-slate-50"}`}>
                    <div className="text-sm font-semibold text-slate-800">{c.tipo}</div>
                    <div className="text-xs mt-0.5 flex items-center gap-2">
                      {mostraMancanti && (
                        <span className="text-red-600">
                          {c.mancanti} <span className="text-slate-400">manca a</span>
                        </span>
                      )}
                      {mostraScaduti && (
                        <span className="text-amber-600">{c.scaduti} scadut{c.scaduti === 1 ? "o" : "i"}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={cerca} onChange={(e) => setCerca(e.target.value)}
                 placeholder="Cerca dipendente per nome, matricola o mansione"
                 className="pl-9 h-11" data-testid="discrepanze-cerca" />
        </div>
        {filtriAttivi && (
          <Button variant="outline" className="h-11" onClick={azzera} data-testid="discrepanze-azzera">
            <X className="w-4 h-4 mr-1" /> Togli i filtri
          </Button>
        )}
      </div>

      {filtriAttivi && (
        <div className="text-xs text-slate-500" data-testid="discrepanze-conteggio-filtrato">
          {visibili.length} dipendent{visibili.length === 1 ? "e" : "i"} su {rows.length}
          {tipo && <> · documento <strong className="text-slate-700">{tipo}</strong></>}
        </div>
      )}

      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="p-4 text-xs text-slate-600">
          <div className="font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-[#0067b2]" /> Cosa viene richiesto
          </div>
          Ogni fascicolo deve contenere <strong>UNILAV, carta d'identità, codice fiscale,
          idoneità sanitaria, consegna DPI</strong> e l'attestato di <strong>formazione
          specifica art. 37</strong>. Tutti gli altri documenti sono facoltativi e non
          vengono segnalati come mancanti. Titolare e impiegate amministrative sono esclusi
          da questi obblighi.
          {categorie && (
            <>
              <button type="button" onClick={() => setMostraCategorie(v => !v)}
                      className="block mt-2 text-orange-600 hover:underline"
                      data-testid="mostra-categorie">
                {mostraCategorie ? "Nascondi" : "Vedi"} come vengono interpretati i documenti in archivio
                ({categorie.length} nomi diversi)
              </button>
              {mostraCategorie && (
                <div className="mt-2 max-h-64 overflow-y-auto border border-slate-200 rounded bg-white">
                  <table className="w-full">
                    <thead className="bg-slate-50 text-slate-500 sticky top-0">
                      <tr>
                        <th className="text-left p-1.5">Nome in archivio</th>
                        <th className="text-right p-1.5">Doc.</th>
                        <th className="text-left p-1.5">Vale come</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categorie.map(c => (
                        <tr key={c.categoria} className="border-t border-slate-100">
                          <td className="p-1.5">{c.categoria}</td>
                          <td className="p-1.5 text-right text-slate-400">{c.documenti}</td>
                          <td className="p-1.5">
                            {c.copre
                              ? <span className="text-emerald-700">{c.copre}</span>
                              : <span className="text-slate-400">facoltativo</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {rows.length === 0 && !caricando && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-8 text-center">
            <div className="w-12 h-12 bg-emerald-500 rounded-full mx-auto mb-3 flex items-center justify-center text-white text-2xl">✓</div>
            <h3 className="font-display text-xl font-bold text-emerald-900">Tutto in regola!</h3>
            <p className="text-sm text-emerald-700 mt-1">Nessuna discrepanza rilevata negli attestati.</p>
          </CardContent>
        </Card>
      )}

      {rows.length > 0 && visibili.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-8 text-center text-sm text-slate-500">
            Nessun dipendente corrisponde ai filtri scelti.
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {visibili.map(r => (
          <Card key={r.employee_id} className="border-slate-200">
            <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="font-bold">{r.full_name}</div>
                <div className="text-xs text-slate-500">{r.badge_number} · {r.role}</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {r.missing.map(m => (
                    <Badge key={m} variant="destructive" className="text-xs">
                      <AlertTriangle className="w-3 h-3 mr-1" /> Mancante: {m}
                    </Badge>
                  ))}
                  {r.expired.map(e => {
                    const obbligatorio = (r.expired_obbligatori || []).includes(e);
                    return (
                      <Badge key={e} className={`border-0 text-xs ${
                        obbligatorio ? "bg-amber-500 text-white" : "bg-slate-200 text-slate-600"}`}>
                        <FileWarning className="w-3 h-3 mr-1" /> Scaduto: {e}
                        {!obbligatorio && " (facoltativo)"}
                      </Badge>
                    );
                  })}
                </div>
              </div>
              <Link to={`/fascicolo/${r.employee_id}`} target="_blank">
                <Button variant="outline" size="sm"><ExternalLink className="w-3 h-3 mr-1" /> Fascicolo</Button>
              </Link>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
